/**
 * @module M/control/PrinterMapControlihm
 */

import JsZip from 'jszip';
import { saveAs } from 'file-saver';
import PrinterMapControlImplihm from '../../impl/ol/js/printermapcontrolihm';
import printermapHTML from '../../templates/printermapihm';
import { getValue } from './i18n/language';

export default class PrinterMapControlihm extends M.Control {
  /**
   * @classdesc
   * Main constructor of the class.
   *
   * @constructor
   * @extends {M.Control}
   * @api stable
   */
  constructor(
    serverUrl,
    mapProxyUrl,
    ENCurl,
    ENCLayer,
    printTemplate,
    printTemplateGeoUrl,
    credits,
    georefActive,
    logoUrl,
    fototeca,
    headerLegend,
    filterTemplates,
    gridStyle,
  ) {
    const impl = new PrinterMapControlImplihm(mapProxyUrl, ENCurl, ENCLayer);

    super(impl, PrinterMapControlihm.NAME);

    impl.facadeControl = this;

    if (M.utils.isUndefined(PrinterMapControlImplihm)) {
      M.exception(getValue('exception.impl'));
    }

    if (M.utils.isUndefined(PrinterMapControlImplihm.prototype.encodeLayer)) {
      M.exception(getValue('exception.encode'));
    }

    /**
     * Mapfish server url
     * @private
     * @type {String}
     */
    this.serverUrl_ = serverUrl;

    this.mapProxyUrl_ = mapProxyUrl;

    this.ENCurl_ = ENCurl;

    this.ENCLayer_ = ENCLayer;

    /**
     * Mapfish template url
     * @private
     * @type {String}
     */
    this.printTemplate_ = printTemplate;

    /**
     * Mapfish template url for georef
     * @private
     * @type {String}
     */
    this.printTemplateGeoUrl_ = printTemplateGeoUrl;


    /**
     * Credits text for template
     * @private
     * @type {String}
     */
    this.credits_ = credits;

    /**
     * Active or disable georeferenced image download
     * @private
     * @type {Boolean}
     */
    this.georefActive_ = georefActive;

    /**
     * Active or disable fototeca fixed description
     * @private
     * @type {Boolean}
     */
    this.fototeca_ = fototeca;

    /**
     * Map title
     * @private
     * @type {HTMLElement}
     */
    this.inputTitle_ = null;

    /**
     * Map description
     * @private
     * @type {HTMLElement}
     */
    this.areaDescription_ = null;

    /**
     * Layout
     * @private
     * @type {HTMLElement}
     */
    this.layout_ = null;

    /**
     * Map format to print
     * @private
     * @type {HTMLElement}
     */
    this.format_ = 'pdf';

    /**
     * Map projection to print
     * @private
     * @type {HTMLElement}
     */
    this.projection_ = null;

    /**
     * Map dpi to print
     * @private
     * @type {HTMLElement}
     */
    this.dpi_ = null;

    /**
     * Max map dpi to print
     * @private
     * @type {HTMLElement}
     */
    this.dpiMax_ = null;

    this.dpiGeo_ = 120;

    /**
     * Keep view boolean
     * @private
     * @type {HTMLElement}
     */
    this.keepView_ = null;

    /**
     * Georref image boolean
     * @private
     * @type {HTMLElement}
     */
    this.georef_ = null;

    this.extentLayer = null;

    /**
     * Mapfish params
     * @private
     * @type {String}
     */
    this.params_ = {
      layout: {
        outputFilename: 'mapa_${yyyy-MM-dd_hhmmss}',
      },
      pages: {
        clientLogo: '', // logo url
        creditos: getValue('credits'),
      },
      parameters: {
        logo: logoUrl,
        headerLegend,
      },
    };

    /**
     * Mapfish params for georef
     * @private
     * @type {String}
     */
    this.paramsGeo_ = {
      layout: {
        outputFilename: 'mapa_${yyyy-MM-dd_hhmmss}',
      },
      pages: {
        clientLogo: '', // logo url
        creditos: getValue('printInfo'),
      },
      parameters: {},
    };

    /**
     * Container of maps available for download
     * @private
     * @type {HTMLElement}
     */
    this.queueContainer_ = null;

    /**
     * Facade of the map
     * @private
     * @type {Promise}
     */
    this.capabilitiesPromise_ = null;

    /**
     * Mapfish options params
     * @private
     * @type {String}
     */
    this.options_ = {
      dpi: 150,
      keepView: true,
      format: 'pdf',
      legend: 'false',
      layout: 'A4 horizontal',
    };

    this.layoutOptions_ = [];
    this.layoutMapSize_ = [];
    this.dpisOptions_ = [];
    // this.outputFormats_ = ['pdf', 'png' /*, 'jpg'*/];
    this.outputFormats_ = ['pdf', 'png'];
    this.documentRead_ = document.createElement('img');
    this.canvas_ = document.createElement('canvas');
    this.proyectionsDefect_ = ['EPSG:25828', 'EPSG:25829', 'EPSG:25830', 'EPSG:25831', 'EPSG:3857', 'EPSG:4326', 'EPSG:4258'];
    this.filterTemplates_ = filterTemplates;
    this.gridStyle_ = gridStyle;

    this.chartIndex = 0;
    this.printStyle = new M.style.Polygon({
      fill: {
        color: '#193063',
        opacity: 0.5,
      },
      stroke: {
        color: '#193063',
        width: '2',
      },
      label: {
        text: '{{titulo}}',
        font: 'bold 16px Courier New',
        align: M.style.align.CENTER,
        baseline: M.style.baseline.MIDDLE,
        color: '#fff',
      },
    });
  }

  /**
   * This function checks when map printing is finished.
   * @param {String} url - Mapfish GET request url
   * @param {Function} callback - function that removes loading icon class.
   */
  getStatus(url, callback) {
    const hayproxy = false; // M.proxy();
    M.proxy(false);
    const param = new Date().getTime();
    M.remote.get(`${url}?timestamp=${param}`).then((response) => {
      const statusJson = JSON.parse(response.text);
      const { status } = statusJson;
      if (status === 'finished') {
        M.proxy(hayproxy);
        callback();
      } else if (status === 'error' || status === 'cancelled') {
        M.proxy(hayproxy);
        callback();
        if (statusJson.error.toLowerCase().indexOf('network is unreachable') > -1 || statusJson.error.toLowerCase().indexOf('illegalargument') > -1) {
          M.dialog.error(getValue('exception.tile'));
        } else {
          M.dialog.error(getValue('exception.error'));
        }

        this.queueContainer_.lastChild.remove();
      } else {
        M.proxy(hayproxy);
        setTimeout(() => this.getStatus(url, callback), 1000);
      }
    });
  }

  /**
   * This function creates the view to the specified map.
   *
   * @public
   * @function
   * @param {M.Map} map to add the control
   * @api stabletrue
   */
  createView(map) {
    const promise = new Promise((success, fail) => {
      this.getCapabilities().then((capabilitiesParam) => {
        const capabilities = capabilitiesParam;
        let i = 0;
        let ilen;

        // default layout
        for (i = 0, ilen = capabilities.layouts.length; i < ilen; i += 1) {
          const layout = capabilities.layouts[i];
          if (layout.name === this.options_.layout) {
            layout.default = true;
            break;
          }
        }

        if (this.filterTemplates_.length > 0) {
          capabilities.layouts = capabilities.layouts.filter((l) => {
            return this.filterTemplates_.indexOf(l.name) > -1;
          });
        }

        // show only template names without 'jpg' on their names
        capabilities.layouts = capabilities.layouts.filter((l) => {
          return !l.name.endsWith('jpg');
        });

        capabilities.layouts.sort((a, b) => {
          let res = 0;
          if (a.name.indexOf('(Perfil') > -1 && b.name.indexOf('(Perfil') === -1) {
            res = 1;
          } else if (a.name.indexOf('(Perfil') === -1 && b.name.indexOf('(Perfil') > -1) {
            res = -1;
          } else if (a.name === b.name) {
            res = 0;
          } else {
            res = a.name < b.name ? 1 : -1;
          }

          return res;
        });

        this.layoutOptions_ = [].concat(capabilities.layouts.map((item) => {
          return item.name;
        }));

        this.layoutMapSize_ = [].concat(capabilities.layouts.map((item) => {
          const mapDef = item.attributes.filter(a => a.name === 'mapDef' || a.name === 'map')[0];
          return `${(mapDef.clientInfo.width ? mapDef.clientInfo.width : 0)};${(mapDef.clientInfo.height ? mapDef.clientInfo.height : 0)}`;
        }));

        capabilities.proyections = [];
        const proyectionsDefect = this.proyectionsDefect_;

        for (i = 0, ilen = proyectionsDefect.length; i < ilen; i += 1) {
          if (proyectionsDefect[i] !== null) {
            const proyection = proyectionsDefect[i];
            const object = { value: proyection };
            if (proyection === 'EPSG:4258') {
              object.default = true;
            }

            capabilities.proyections.push(object);
          }
        }

        capabilities.dpis = [];
        let attribute;
        // default dpi
        // recommended DPI list attribute search
        for (i = 0, ilen = capabilities.layouts[0].attributes.length; i < ilen; i += 1) {
          if (capabilities.layouts[0].attributes[i].clientInfo !== undefined) {
            attribute = capabilities.layouts[0].attributes[i];
            this.dpiMax_ = attribute.clientInfo.maxDPI;
          }
        }

        for (i = 1, ilen = attribute.clientInfo.dpiSuggestions.length; i < ilen; i += 1) {
          const dpi = attribute.clientInfo.dpiSuggestions[i];

          if (parseInt(dpi, 10) === this.options_.dpi) {
            dpi.default = true;
            break;
          }
          const object = { value: dpi };
          capabilities.dpis.push(object);
        }

        this.dpisOptions_ = [].concat(capabilities.dpis.map((item) => {
          return item.value;
        }));

        if (Array.isArray(capabilities.formats)) {
          this.outputFormats_ = capabilities.formats.filter((f) => {
            return f !== 'jpg';
          });
        }

        capabilities.format = this.outputFormats_.map((format) => {
          return {
            name: format,
            default: format === 'pdf',
          };
        });

        // keepView
        capabilities.keepView = this.options_.keepView;

        // georefActive
        capabilities.georefActive = this.georefActive_;

        // fototeca
        capabilities.fototeca = this.fototeca_;

        // translations
        capabilities.translations = {
          tooltip: getValue('tooltip'),
          title: getValue('title'),
          description: getValue('description'),
          layout: getValue('layout'),
          scale: getValue('scale'),
          format: getValue('format'),
          projection: getValue('projection'),
          keep: getValue('keep'),
          geo: getValue('geo'),
          print: getValue('print'),
          delete: getValue('delete'),
          download: getValue('download'),
          minimize: getValue('minimize'),
          fototeca: getValue('fototeca'),
          move: getValue('move'),
          select_extent: getValue('select_extent'),
          clean: getValue('clean'),
          draw_figure: getValue('draw_figure'),
          queue: getValue('queue'),
          impress_attr: getValue('impress_attr'),
          calc: getValue('calc'),
          proj_message: getValue('proj_message'),
        };

        const html = M.template.compileSync(printermapHTML, {
          jsonp: true,
          vars: capabilities,
        });
        this.addEvents(html);
        success(html);
      });
    });
    return promise;
  }

  /**
   * This function adds event listeners.
   *
   * @public
   * @function
   * @param {M.Map} map to add the control
   * @api stable
   */
  addEvents(html) {
    this.element_ = html;
    this.inputTitle_ = this.element_.querySelector('.form div.title > input');
    this.areaDescription_ = this.element_.querySelector('.form div.description > textarea');
    const selectLayout = this.element_.querySelector('.form div.layout > select');
    selectLayout.addEventListener('change', (e) => {
      const layoutValue = selectLayout.value;
      this.setLayout({
        value: layoutValue,
        name: layoutValue,
      });
    });

    const layoutValue = selectLayout.value;
    this.setLayout({
      value: layoutValue,
      name: layoutValue,
    });

    const selectDpi = this.element_.querySelector('.form div.dpi > select');
    selectDpi.addEventListener('change', (e) => {
      const dpiValue = selectDpi.value;
      this.setDpi({
        value: dpiValue,
        name: dpiValue,
      });
    });

    const dpiValue = selectDpi.value;
    this.setDpi({
      value: dpiValue,
      name: dpiValue,
    });

    const selectProjection = this.element_.querySelector('#projections');
    this.showHideProjectionWarning();
    selectProjection.addEventListener('change', this.showHideProjectionWarning.bind(this));

    const printBtn = this.element_.querySelector('#print-btn');
    printBtn.addEventListener('click', this.printClick_.bind(this));

    const cleanBtn = this.element_.querySelector('#delete-btn');
    cleanBtn.addEventListener('click', this.clearForm.bind(this));

    this.queueContainer_ = this.element_.querySelector('#queue-div');
    M.utils.enableTouchScroll(this.queueContainer_);

    this.element_.querySelector('#attributes').addEventListener('click', this.toggleTabs);
    this.element_.querySelector('#queue').addEventListener('click', this.toggleTabs);
    this.element_.querySelector('#select-extent').addEventListener('click', this.addMapClickEvent.bind(this));
    this.element_.querySelector('#draw-figure').addEventListener('click', this.addMapFigureEvent.bind(this));
    this.element_.querySelector('#queue-table').addEventListener('click', this.selectQueueElement.bind(this));
    this.element_.querySelector('#queue-delete').addEventListener('click', this.deleteFromQueue.bind(this));
    const moveTools = this.element_.querySelectorAll('.move-extent');
    moveTools.forEach((t) => {
      t.addEventListener('click', this.toggleMoveTool.bind(this));
    });
    this.createLayer();
  }

  showHideProjectionWarning() {
    const selectProjection = this.element_.querySelector('#projections').value;
    const warningDiv = this.element_.querySelector('#projection-warning');

    if (selectProjection === 'EPSG:3857' && !warningDiv.classList.contains('hide')) {
      warningDiv.classList.add('hide');
    } else {
      warningDiv.classList.remove('hide');
    }
  }

  /**
   * Sets layout
   *
   * @private
   * @function
   */
  setLayout(layout) {
    this.layout_ = layout;
  }

  /**
   * Sets format
   *
   * @private
   * @function
   */
  setFormat(format) {
    this.format_ = format;
  }

  /**
   * Sets projection
   *
   * @private
   * @function
   */
  setProjection(projection) {
    this.projection_ = projection;
  }

  /**
   * Sets dpi
   *
   * @private
   * @function
   */
  setDpi(dpi) {
    this.dpi_ = dpi;
  }

  /**
   * Sets keep view option
   *
   * @private
   * @function
   */
  setKeepView(keepView) {
    this.keepView_ = keepView;
  }

  /**
   * Sets georef image option
   *
   * @private
   * @function
   */
  setGeoref(georef) {
    this.georef_ = georef;
  }

  /**
   * This function prints on click
   *
   * @private
   * @function
   */
  printClick_(evt) {
    evt.preventDefault();
    const elements = document.querySelectorAll('#queue-table>tr.selected');
    if (elements.length > 0) {
      elements.forEach((e) => {
        e.classList.remove('selected');
        const f = this.extentLayer.getFeatureById(e.id);
        this.printFeature(e, f);
      });
    } else {
      M.dialog.info('Debe seleccionar los elementos que se van a imprimir');
    }
  }

  printFeature(queueEl, feature) {
    let getPrintData;
    let printUrl;
    let download;
    if (this.georef_) {
      getPrintData = this.getPrintDataGeo(feature);
      printUrl = this.printTemplateGeoUrl_;
    } else {
      getPrintData = this.getPrintData(feature);
      printUrl = `${this.serverUrl_}/print/${this.printTemplate_}`;
      download = this.downloadPrint;
    }

    getPrintData.then((printData) => {
      if (this.georef_) {
        download = this.downloadGeoPrint.bind(this, printData.attributes.map.bbox);
      }

      let url = M.utils.concatUrlPaths([printUrl, `report.${printData.outputFormat}`]);

      queueEl.lastChild.classList.add(PrinterMapControlihm.LOADING_CLASS);
      queueEl.lastChild.classList.remove('finished');
      url = M.utils.addParameters(url, 'mapeaop=geoprint');
      const profilControl = this.map_.getMapImpl().getControls().getArray().filter((c) => {
        return c.element !== undefined && c.element.classList !== undefined && c.element.classList.contains('ol-profil');
      });

      if ((this.georef_ === null || !this.georef_) && profilControl.length > 0) {
        // eslint-disable-next-line no-param-reassign
        printData.attributes.profil = profilControl[0].getImage();
      }

      // FIXME: delete proxy deactivation and uncomment if/else when proxy is fixed on Mapea
      const hayproxy = false; // M.proxy();
      M.proxy(false);
      M.remote.post(url, printData).then((responseParam) => {
        let response = responseParam;
        const responseStatusURL = JSON.parse(response.text);
        const ref = responseStatusURL.ref;
        const statusURL = `${this.serverUrl_}/print/status/${ref}.json`;
        this.getStatus(statusURL, () => {
          queueEl.lastChild.classList.remove(PrinterMapControlihm.LOADING_CLASS);
          queueEl.lastChild.classList.add('finished');
        });
        // if (response.error !== true) { // withoud proxy, response.error === true
        let downloadUrl;
        try {
          response = JSON.parse(response.text);
          const imageUrl = response.downloadURL.substring(response.downloadURL.indexOf('/print/'), response.downloadURL.length);
          downloadUrl = M.utils.concatUrlPaths([this.serverUrl_, imageUrl]);
          this.documentRead_.src = downloadUrl;
        } catch (err) {
          M.exception(err);
        }
        queueEl.lastChild.setAttribute(PrinterMapControlihm.DOWNLOAD_ATTR_NAME, downloadUrl);
        queueEl.lastChild.addEventListener('click', download);
        // } else {
        //  M.dialog.error('Se ha producido un error en la impresión.');
        // }
      });
      M.proxy(hayproxy);
    });
  }

  /**
   * Gets capabilities
   *
   * @public
   * @function
   * @param {M.Map} map to add the control
   * @api stable
   */
  getCapabilities() {
    if (M.utils.isNullOrEmpty(this.capabilitiesPromise_)) {
      this.capabilitiesPromise_ = new Promise((success, fail) => {
        const capabilitiesUrl = M.utils.concatUrlPaths([`${this.serverUrl_}/print/${this.printTemplate_}`, 'capabilities.json']);
        const hayproxy = false; // M.proxy();
        M.proxy(false);
        M.remote.get(capabilitiesUrl).then((response) => {
          let capabilities = {};
          try {
            capabilities = JSON.parse(response.text);
          } catch (err) {
            M.exception(err);
          }
          success(capabilities);
        });

        M.proxy(hayproxy);
      });
    }
    return this.capabilitiesPromise_;
  }

  /**
   * Converts decimal degrees coordinates to degrees, minutes, seconds
   * @public
   * @function
   * @param {String} coordinate - single coordinate (one of a pair)
   * @api
   */
  converterDecimalToDMS(coordinate, nsew) {
    let dms;
    let aux;
    let label;
    if (nsew === 'NS') {
      label = coordinate > 0 ? 'N' : 'S';
    } else {
      label = coordinate > 0 ? 'E' : 'W';
    }
    const coord = Math.abs(coordinate).toString();
    const splittedCoord = coord.split('.');
    // Degrees
    dms = nsew === 'NS' ? `${splittedCoord[0].padStart(2, 0)}°` : `${splittedCoord[0].padStart(3, 0)}°`;
    // Minutes
    aux = `0.${splittedCoord[1]}`;
    aux *= 60;
    aux = aux.toString();
    aux = aux.split('.');
    dms = `${dms}${aux[0].padStart(2, 0)}'`;
    // Seconds
    aux = `0.${aux[1]}`;
    aux *= 60;
    aux = aux.toFixed(1).toString();
    aux = aux.split('.');
    dms = `${dms}${aux[0].padStart(2, 0)}.${aux[1]}"${label}`;
    return dms;
  }

  /**
   * Converts original bbox coordinates to DMS coordinates.
   * @public
   * @function
   * @api
   * @param {Array<Object>} bbox - { x: {min, max}, y: {min, max} }
   */
  convertBboxToDMS(bbox) {
    const proj = this.map_.getProjection();
    let dmsBbox = bbox;
    if (proj.units === 'm') {
      const min = [bbox.x.min, bbox.y.min];
      const max = [bbox.x.max, bbox.y.max];
      const newMin = this.getImpl().reproject(proj.code, min);
      const newMax = this.getImpl().reproject(proj.code, max);
      dmsBbox = {
        x: {
          min: newMin[0],
          max: newMax[0],
        },
        y: {
          min: newMin[1],
          max: newMax[1],
        },
      };
    }

    dmsBbox = this.convertDecimalBoxToDMS(dmsBbox);
    return dmsBbox;
  }

  /**
   * Converts original bbox coordinates to DMS coordinates.
   * @public
   * @function
   * @api
   * @param {Array<Object>} bbox - { x: {min, max}, y: {min, max} }
   */
  convertArrayBboxToDMS(bbox) {
    const proj = this.map_.getProjection();
    let dmsBbox = bbox;
    if (proj.units === 'm') {
      const min = [bbox[0], bbox[1]];
      const max = [bbox[2], bbox[3]];
      const newMin = this.getImpl().reproject(proj.code, min);
      const newMax = this.getImpl().reproject(proj.code, max);
      dmsBbox = {
        x: {
          min: newMin[0],
          max: newMax[0],
        },
        y: {
          min: newMin[1],
          max: newMax[1],
        },
      };
    }
    // console.log(dmsBbox);
    dmsBbox = this.convertDecimalBoxToDMS(dmsBbox);
    return dmsBbox;
  }
  convertCoordsToDMS(coords) {
    const proj = this.map_.getProjection();
    let dmsCoords = coords;
    if (proj.units === 'm') {
      dmsCoords = this.getImpl().reproject(proj.code, dmsCoords);
    }
    const dmsX = this.converterDecimalToDMS(dmsCoords[0], 'EW');
    const dmsY = this.converterDecimalToDMS(dmsCoords[1], 'NS');
    return [dmsX, dmsY];
  }

  /**
   * Converts decimal coordinates Bbox to DMS coordinates Bbox.
   * @public
   * @function
   * @api
   * @param { Array < Object > } bbox - { x: { min, max }, y: { min, max } }
   */
  convertDecimalBoxToDMS(bbox) {
    return {
      x: {
        min: this.converterDecimalToDMS(bbox.x.min, 'EW'),
        max: this.converterDecimalToDMS(bbox.x.max, 'EW'),
      },
      y: {
        min: this.converterDecimalToDMS(bbox.y.min, 'NS'),
        max: this.converterDecimalToDMS(bbox.y.max, 'NS'),
      },
    };
  }

  /**
   * This function returns request JSON.
   *
   * @private
   * @function
   */
  getPrintDataOld(feature) {
    const title = feature.getAttribute('titulo');
    let description = feature.getAttribute('descripcion');
    const credits = this.credits_;
    if (credits.length > 2) {
      if (description.length > 0) {
        description += ` - ${credits}`;
      } else {
        description += credits;
      }
    }

    const projection = this.map_.getProjection().code;
    const bbox = feature.getImpl().getOLFeature().getGeometry().getExtent();
    const dmsBbox = this.convertArrayBboxToDMS(bbox);
    let layout = feature.getAttribute('plantilla');
    let dpi;
    if (!this.keepView_) {
      dpi = feature.getAttribute('dpi');
    } else {
      dpi = 120;
    }
    const outputFormat = this.format_;
    const parameters = this.params_.parameters;
    const attributionContainer = document.querySelector('#m-attributions-container>div>a');
    const attribution = attributionContainer !== null ?
      `${getValue('base')}: ${attributionContainer.innerHTML}` : '';

    if (outputFormat === 'jpg') {
      layout += ' jpg';
    }

    const date = new Date();
    const currentDate = ''.concat(date.getDate(), '/', date.getMonth() + 1, '/', date.getFullYear());
    let fileTitle = title.replace(' ', '');
    if (fileTitle.length <= 8) {
      fileTitle = fileTitle.concat('${yyyyMMddhhmmss}');
      this.params_.layout.outputFilename = fileTitle;
    } else {
      fileTitle = fileTitle.substring(0, 7).concat('${yyyyMMddhhmmss}');
      this.params_.layout.outputFilename = fileTitle;
    }

    const printData = M.utils.extend({
      layout,
      outputFormat,
      attributes: {
        title,
        description,
        attributionInfo: attribution,
        refsrs: this.turnProjIntoLegend(projection),
        printDate: currentDate,
        map: {
          dpi,
          projection,
          useAdjustBounds: true,
        },
        xCoordTopLeft: dmsBbox.y.max,
        yCoordTopLeft: dmsBbox.x.min,
        xCoordTopRight: dmsBbox.y.max,
        yCoordTopRight: dmsBbox.x.max,
        xCoordBotRight: dmsBbox.y.min,
        yCoordBotRight: dmsBbox.x.max,
        xCoordBotLeft: dmsBbox.y.min,
        yCoordBotLeft: dmsBbox.x.min,
      },
    }, this.params_.layout);

    return this.encodeLayers().then((encodedLayers) => {
      // Se filtra por si se ha eliminado algun layer debido a un error
      printData.attributes.map.layers = encodedLayers.filter(l => l);
      printData.attributes = Object.assign(printData.attributes, parameters);
      if (projection !== 'EPSG:3857' && this.map_.getLayers().some(layer => (layer.type === M.layer.type.OSM || layer.type === M.layer.type.Mapbox))) {
        printData.attributes.map.projection = 'EPSG:3857';
      }

      printData.attributes.map.bbox = [bbox[0], bbox[1], bbox[2], bbox[3]];
      if (projection !== 'EPSG:3857' && this.map_.getLayers().some(layer => (layer.type === M.layer.type.OSM || layer.type === M.layer.type.Mapbox))) {
        printData.attributes.map.bbox = this.getImpl().transformExt(printData.attributes.map.bbox, projection, 'EPSG:3857');
      }

      return printData;
    });
  }

  transformBboxToCustom(bbox, lat) {
    const proj4Str = `+proj=merc +lat_ts=${lat} +lon_0=0 +datum=WGS84 +units=m +no_defs`;
    // const mapProj = this.map_.getProjection().code;
    const bboxMin = proj4('EPSG:3857', proj4Str, [bbox[0], bbox[1]]);
    const bboxMax = proj4('EPSG:3857', proj4Str, [bbox[2], bbox[3]]);

    return bboxMin.concat(bboxMax);
  }

  getBboxFromFeature(feature) {
    let realFeature = feature;
    if (feature.finalCoords) {
      this.setFinalBbox(feature);
      const geometry = { type: 'Polygon', coordinates: feature.finalCoords };
      realFeature = new M.Feature();
      realFeature.setGeometry(geometry);
    }
    const bbox = realFeature.getImpl().getOLFeature().getGeometry().getExtent();
    return bbox;
  }

  getPrintData(feature) {
    const title = feature.getAttribute('titulo');
    let description = feature.getAttribute('descripcion');
    if (this.credits_.length > 2) {
      if (description.length > 0) {
        description += ` - ${this.credits_}`;
      } else {
        description = this.credits_;
      }
    }
    // const projection = this.map_.getProjection().code;
    const bbox = this.getBboxFromFeature(feature);
    const dmsBbox = this.convertArrayBboxToDMS(bbox);
    let layout = feature.getAttribute('plantilla');
    const dpi = feature.getAttribute('dpi');
    const scale = feature.getAttribute('escala');
    const projectionVal = feature.getAttribute('proyeccion');
    const centerLonLat = this.getCenterByBbox(bbox);
    let bboxPrint = bbox;
    let projectionInfo = 'WGS 1984 Pseudo Mercator (EPSG:3857)';
    if (projectionVal === 'EPSG:33952') {
      bboxPrint = this.transformBboxToCustom(bbox, centerLonLat[1]);
      const paraleloRef = this.getParaleloRef(centerLonLat[1]);
      projectionInfo = `WGS 1984 World Mercator (Calculated) \n at Lat ${paraleloRef} GCS WGS 1984`;
    }

    const dBboxXmaxbbox = bbox[2];
    const dBboxXminbbox = bbox[0];
    const dBboxYmaxbbox = bbox[3];
    const dBboxYminbbox = bbox[1];
    const outputFormat = this.format_;

    if (outputFormat === 'jpg') {
      layout += ' jpg';
    }

    let fileTitle = title.replace(' ', '');
    if (fileTitle.length <= 8) {
      fileTitle = fileTitle.concat('${ddMMyyyy-hhmmss}');
      this.params_.layout.outputFilename = fileTitle;
    } else {
      fileTitle = fileTitle.substring(0, 7).concat('${${ddMMyyyy-hhmmss}}');
      this.params_.layout.outputFilename = fileTitle;
    }

    const lenx = dBboxXmaxbbox - dBboxXminbbox;
    const leny = dBboxYmaxbbox - dBboxYminbbox;
    const origen = [(dBboxXmaxbbox + dBboxXminbbox) / 2, (dBboxYmaxbbox + dBboxYminbbox) / 2];
    const origenDMS = this.convertCoordsToDMS(origen);

    let lines = [5, 5];
    if (layout.includes('Horizontal')) {
      lines = [6, 5];
    } else if (layout.includes('Vertical')) {
      lines = [5, 6];
    }
    let espaciado = [(lenx / 5).toFixed(0), (leny / 5).toFixed(0)];
    espaciado = lines;

    let XCoordLeft1Value = 0;
    let XCoordLeft2Value = 0;
    let YCoordBot1Value = 0;
    let YCoordBot2Value = 0;
    let XCoordRight1Value = 0;
    let XCoordRight2Value = 0;
    let YCoordTop1Value = 0;
    let YCoordTop2Value = 0;
    let YCoordTopValue = 0;
    let YCoordBotValue = 0;
    let XCoordLeftValue = 0;
    let XCoordRightValue = 0;
    const XCoordCenterValue = origenDMS[0].replace(/0+/g, '0');
    const YCoordCenterValue = origenDMS[1];
    let YCoordTop = 0;
    let YCoordBot = 0;
    let XCoordRight = 0;
    let XCoordLeft = 0;
    if (layout.includes('Horizontal')) {
      espaciado = [(lenx / 5), (leny / 3)];

      XCoordLeft1Value = origen[0] - espaciado[0];
      XCoordLeft2Value = XCoordLeft1Value - espaciado[0];
      XCoordRight1Value = origen[0] + espaciado[0];
      XCoordRight2Value = XCoordRight1Value + espaciado[0];
      YCoordTop = origen[1] + espaciado[1];
      YCoordBot = origen[1] - espaciado[1];
      // const DmsCoordsAux = this.convertCoordsToDMS([XCoordLeft1Value, YCoordTopValue]);
      // M.dialog.info(`XCoordLeft1 ${DmsCoordsAux[0]}`);
      // M.dialog.info(`YCoordTopValue ${DmsCoordsAux[1]}`);

      const DmsCoordsXCoordLeft1 = this.convertCoordsToDMS([XCoordLeft1Value, YCoordTop]);
      XCoordLeft1Value = DmsCoordsXCoordLeft1[0].replace(/0+/g, '0');
      YCoordTopValue = DmsCoordsXCoordLeft1[1];
      const DmsCoordsXCoordLeft2 = this.convertCoordsToDMS([XCoordLeft2Value, YCoordTop]);
      XCoordLeft2Value = DmsCoordsXCoordLeft2[0].replace(/0+/g, '0');
      const DmsCoordsXCoordRight1 = this.convertCoordsToDMS([XCoordRight1Value, YCoordBot]);
      XCoordRight1Value = DmsCoordsXCoordRight1[0].replace(/0+/g, '0');
      YCoordBotValue = DmsCoordsXCoordRight1[1];
      const DmsCoordsXCoordRight2 = this.convertCoordsToDMS([XCoordRight2Value, YCoordBot]);
      XCoordRight2Value = DmsCoordsXCoordRight2[0].replace(/0+/g, '0');

      // M.dialog.info(`YCoordBotValue ${YCoordBotValue}`);
      // M.dialog.info(`YCoordBotValue ${YCoordTopValue}`);
      // M.dialog.info(`YCoordBotValue ${YCoordCenterValue}`);
      // const DmsCoordsYTopBot = this.convertCoordsToDMS([YCoordTopValue, YCoordBotValue]);
      // YCoordTopValue = DmsCoordsYTopBot[0];
      // YCoordBotValue = DmsCoordsYTopBot[1];
    } else if (layout.includes('Vertical')) {
      espaciado = [(lenx / 3), (leny / 5)];

      YCoordBot1Value = origen[1] - espaciado[1];
      YCoordBot2Value = YCoordBot1Value - espaciado[1];
      YCoordTop1Value = origen[1] + espaciado[1];
      YCoordTop2Value = YCoordTop1Value + espaciado[1];

      XCoordRight = origen[0] + espaciado[0];
      XCoordLeft = origen[0] - espaciado[0];

      const DmsCoordsYCoordTop2 = this.convertCoordsToDMS([XCoordLeft, YCoordTop2Value]);
      XCoordLeftValue = DmsCoordsYCoordTop2[0].replace(/0+/g, '0');
      YCoordTop2Value = DmsCoordsYCoordTop2[1];
      const DmsCoordsYCoordTop1 = this.convertCoordsToDMS([XCoordLeft, YCoordTop1Value]);
      YCoordTop1Value = DmsCoordsYCoordTop1[1];
      const DmsCoordsYCoordBot1 = this.convertCoordsToDMS([XCoordRight, YCoordBot1Value]);
      XCoordRightValue = DmsCoordsYCoordBot1[0].replace(/0+/g, '0');
      YCoordBot1Value = DmsCoordsYCoordBot1[1];
      const DmsCoordsYCoordBot2 = this.convertCoordsToDMS([XCoordRight, YCoordBot2Value]);
      YCoordBot2Value = DmsCoordsYCoordBot2[1];
    }

    const printData = {
      outputFormat,
      layout,
      outputFilename: this.params_.layout.outputFilename,
      attributes: {
        title,
        description,
        currentsSignsText: 'SIGNOS CONVENCIONALES',
        currentsSignsTextEnglish: 'LEGEND',
        logo: './imagenes/logo_IHM.svg',
        projectionInfo,
        mapDef: {
          bbox: bboxPrint,
          dpi,
          projection: projectionVal,
          centerEpsg: centerLonLat[1],
          useAdjustBounds: true,
          rotation: 0,
          scale,
        },
        legend: this.getLegendData(),
        xCoordTopLeft: dmsBbox.y.max.replace(/0+/g, '0'),
        yCoordTopLeft: dmsBbox.x.min.replace(/0+/g, '0'),
        xCoordTopRight: bboxPrint[2].toFixed(0).toString(), // dmsBbox.y.max.replace(/0+/g, '0'),
        yCoordTopRight: bboxPrint[3].toFixed(0).toString(), // dmsBbox.x.max.replace(/0+/g, '0'),
        // xCoordTopRight: dBboxXmax.toFixed(0),
        // yCoordTopRight: dBboxYmax.toFixed(0),
        xCoordBotRight: dmsBbox.y.min.replace(/0+/g, '0'),
        yCoordBotRight: dmsBbox.x.max.replace(/0+/g, '0'),
        xCoordBotLeft: bboxPrint[0].toFixed(0).toString(), // dmsBbox.y.min.replace(/0+/g, '0'),
        yCoordBotLeft: bboxPrint[1].toFixed(0).toString(), // dmsBbox.x.min.replace(/0+/g, '0'),
        // xCoordBotLeft: dBboxXmin.toFixed(0),
        // yCoordBotLeft: dBboxYmin.toFixed(0),
        // HORIZONTAL
        XCoordLeft2: XCoordLeft2Value,
        XCoordLeft1: XCoordLeft1Value,
        XCoordCenter: XCoordCenterValue,
        XCoordRight1: XCoordRight1Value,
        XCoordRight2: XCoordRight2Value,
        YCoordTop: YCoordTopValue,
        YCoordCenter: YCoordCenterValue,
        YCoordBot: YCoordBotValue,
        // XCoordCenterT: XCoordCenterValue.replace(/0+/g, '0'),
        // VERTICAL
        XCoordLeft: XCoordLeftValue,
        // XCoordCenter: XCoordCenterValue.replace(/0+/g, '0'), duplicated: se pone arriba
        XCoordRight: XCoordRightValue,
        YCoordTop2: YCoordTop2Value,
        YCoordTop1: YCoordTop1Value,
        // YCoordCenter: YCoordCenterValue, duplicated: se pone arriba
        YCoordBot1: YCoordBot1Value,
        YCoordBot2: YCoordBot2Value,
      },
    };

    return this.encodeLayers(bbox, projectionVal).then((encodedLayers) => {
      // Se filtra por si se ha eliminado algun layer debido a un error
      printData.attributes.mapDef.layers = encodedLayers.filter(l => l);
      // printData.attributes.mapDef.layers.unshift(gridLayer);
      // printData.attributes = Object.assign(printData.attributes, parameters);
      /* if (projection !== 'EPSG:3857' && this.map_.getLayers().some(layer =>
        (layer.type === M.layer.type.OSM || layer.type === M.layer.type.Mapbox))) {
        printData.attributes.mapDef.projection = 'EPSG:3857';
      }

      if (projection !== 'EPSG:3857' && this.map_.getLayers().some(layer =>
        (layer.type === M.layer.type.OSM || layer.type === M.layer.type.Mapbox))) {
        printData.attributes.mapDef.bbox = this.getImpl().transformExt(
          printData.attributes.mapDef.bbox, projection, 'EPSG:3857');
      } */

      return printData;
    });
  }

  getLegendData() {
    const classes = [];
    const legend = {
      name: '',
    };
    const layers = this.map_.getLayers().filter((layer) => {
      return (layer.isVisible() && layer.inRange() && layer.name !== 'cluster_cover' &&
      layer.name !== 'selectLayer' && layer.name !== 'empty_layer' && layer.name !== 'ExtentLayer');
    });

    layers.forEach((l) => {
      if (l.name !== '__draw__' && l.name !== 'IGNBaseTodo') {
        const srcValue = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMgAAAAyCAYAAAAZUZThAAAAAXNSR0IArs4c6QAAA/5JREFUeF7tmjFME1EYx993BR2qEw7EhboQFo3YsrgAOy6OhoSygMxuLsDC5ow0JgVjHF3UGbroQCsGF0NMgBgMC4uAiZa7Z96Ta45C69drSa/3/Vn53vX9f9/93r13LSn8xZpA+nkxT6R3i9NDc40GzSyuj2iH8qT0SpjxjX5eFOupelJ3FzdSXc5JVmvqM//TDhU811n7PDO4E8UAmFN9Apml9TmtaFYpteN6iVFuH4PjlFKpRsfHpS9nBAlAUckrzsH1K4mD/aNyfxfRnqu9F3FaRe7lSllSeqJVjSStC1Hlc/okWDU3uSaa/zSVXq6V2yyQCcc1tSlSet5kCitZq9i28zoVQXwIvde6t949uvPMn9T63mHPy839+x+//xzzgbVzwq367HSuuKq0GjF5m73m4R+357jsfSlNZUabvdZljQ/c+KrWlikoAnl6sjgztObPhzP+subezutaQU7Db1fLEZzY+JuvE98OfvW5rjceBNfOyTfz2UaQ3mT3zeBiEPZ6Y683n+wfl39EWRC/z3b7rGg2uNjZ/ifcvFkwFKm1WjlsHbmzipR5+tqnS1hmnTLOCmK3G1rni1Pp6VoTN0+SmfdbC3EBI1EQv7fBJ4XZcpnem/9xels5o4Y413SKFMF5WkHMm47kVedBITv4tF6ITlkpOY2QLEhg12DPGmEO4P6iasaWpjO3OMw7seafILniarLbuf0/QYaXNxaOf3tvS48zk50YNjhn6YL4kjgJb6Teob1en+3TRJ2k4rDlrpXTCuI/cuttsWxdrrSkiSbDAo2SVBAkSt2I7lzOHNIHbiQ/vHo4sHLRdO326qjcT54ejcOKAUGie1NGaWaV17z+nrJaEnM4ny9sZ60cMXpzAUGidBtGdy4VQareTpz5opD7hiO6Mc/PzD93mS9Dm523WTzqvR5t9voY3z4CF/7UxBzcyNPDVgzSuyde1zL3Jwrti9LYJ7f6m3StaCUOZ7PGKMa/+pwg8Y+MhCDAJwBB+KxQKZAABBHYdETmE4AgfFaoFEgAgghsOiLzCUAQPitUCiQAQQQ2HZH5BCAInxUqBRKAIAKbjsh8AhCEzwqVAglAEIFNR2Q+AQjCZ4VKgQQgiMCmIzKfAAThs0KlQAIQRGDTEZlPAILwWaFSIAEIIrDpiMwnAEH4rFApkAAEEdh0ROYTgCB8VqgUSACCCGw6IvMJQBA+K1QKJABBBDYdkfkEIAifFSoFEoAgApuOyHwCEITPCpUCCUAQgU1HZD4BCMJnhUqBBCCIwKYjMp8ABOGzQqVAAhBEYNMRmU8AgvBZoVIgAQgisOmIzCcAQfisUCmQAAQR2HRE5hOAIHxWqBRIAIIIbDoi8wn8BdmBukJUTnDxAAAAAElFTkSuQmCC';
        const legendURL = l.getLegendURL();
        if (legendURL instanceof Promise && legendURL.constructor && legendURL.constructor.name === 'Promise') {
          classes.push({
            name: l.name,
            icons: [srcValue],
          });
        } else {
          classes.push({
            name: l.name,
            icons: [legendURL],
          });
        }
      }
    });

    legend.classes = classes;
    return legend;
  }

  getCenterByBbox(bbox) {
    const center = [(bbox[0] + bbox[2]) / 2, (bbox[1] + bbox[3]) / 2];
    return ol.proj.transform(center, this.map_.getProjection().code, 'EPSG:4326');
  }

  getParaleloRef(lat) {
    // const grados = Number.parseInt(lat, 10);
    // const min = Number.parseInt((lat - grados) * 60, 10);
    // const seg = Number.parseInt((((lat - grados) * 60) - min) * 60, 10);

    // return `${grados}° ${min}' ${seg}''`;
    return this.converterDecimalToDMS(lat, 'NS');
  }

  /**
   * This function returns request JSON for georef image.
   *
   * @private
   * @function
   */
  getPrintDataGeo(feature) {
    let projection;
    if (this.projection_.value === 'EPSG:4326' || this.projection_.value === 'EPSG:4258') {
      projection = this.map_.getProjection().code;
      this.projection_.value = projection;
    } else {
      projection = this.projection_.value;
    }

    const bbox = feature.getImpl().getOLFeature().getGeometry().getExtent();
    const width = this.map_.getMapImpl().getSize()[0];
    const height = this.map_.getMapImpl().getSize()[1];
    const layout = 'plain';
    // const dpi = this.dpiMax_;
    const dpi = this.dpiGeo_;
    const outputFormat = 'jpg';
    const parameters = this.paramsGeo_.parameters;
    const printData = M.utils.extend({
      layout,
      outputFormat,
      attributes: {
        map: {
          dpi,
          projection,
        },
      },
    }, this.paramsGeo_.layout);

    return this.encodeLayersGeo().then((encodeLayersGeo) => {
      const returnData = encodeLayersGeo;
      let encodedLayersModified = [];
      if (projection === 'EPSG:25830') {
        for (let i = 0; i < returnData.length; i += 1) {
          if (returnData[i].matrixSet != null) {
            const matrixSet = returnData[i].matrixSet.replace('GoogleMapsCompatible', 'EPSG:25830');
            returnData[i].matrixSet = matrixSet;
          }
          encodedLayersModified.push(returnData[i]);
        }
      } else {
        encodedLayersModified = encodeLayersGeo;
      }

      printData.attributes.map.layers = encodedLayersModified;
      printData.attributes = Object.assign(printData.attributes, parameters);
      printData.attributes.map.projection = projection;
      printData.attributes.map.dpi = dpi;
      printData.attributes.map.width = width;
      printData.attributes.map.height = height;
      printData.attributes.map.bbox = [bbox[0], bbox[1], bbox[2], bbox[3]];
      if (this.map_.getProjection().code !== projection) {
        printData.attributes.map.bbox = this.getImpl().transformExt(
          printData.attributes.map.bbox, this.map_.getProjection().code,
          projection,
        );
      }

      return printData;
    });
  }

  /**
   * This function encodes layers.
   *
   * @private
   * @function
   */
  encodeLayers(bbox, projection) {
    // Filters visible layers whose resolution is inside map resolutions range
    // and that doesn't have Cluster style.
    let layers = this.map_.getLayers().filter((layer) => {
      return (layer.isVisible() && layer.inRange() && layer.name && layer.name !== 'cluster_cover' &&
      layer.name !== 'selectLayer' && layer.name !== 'empty_layer' && layer.name !== 'ExtentLayer');
    });

    if (this.map_.getZoom() === 20) {
      let contains = false;
      layers.forEach((l) => {
        if (l.url !== undefined && l.url === 'https://tms-pnoa-ma.idee.es/1.0.0/pnoa-ma/{z}/{x}/{-y}.jpeg') {
          contains = true;
        }
      });

      if (contains) {
        layers = layers.filter((l) => {
          return l.url !== 'https://tms-pnoa-ma.idee.es/1.0.0/pnoa-ma/{z}/{x}/{-y}.jpeg';
        });
      }
    } else if (this.map_.getZoom() < 20) {
      let contains = false;
      layers.forEach((l) => {
        if (l.url !== undefined && l.name !== undefined && l.url === 'https://www.ign.es/wmts/pnoa-ma?' && l.name === 'OI.OrthoimageCoverage') {
          contains = true;
        }
      });

      if (contains) {
        layers = layers.filter((l) => {
          return l.url !== 'https://www.ign.es/wmts/pnoa-ma?' && l.name !== 'OI.OrthoimageCoverage';
        });
      }
    }

    let numLayersToProc = layers.length;
    const otherLayers = this.getImpl().getParametrizedLayers('IMAGEN', layers);
    if (otherLayers.length > 0) {
      layers = layers.concat(otherLayers);
      numLayersToProc = layers.length;
    }

    layers = layers.sort((a, b) => {
      let res = 0;
      const zia = a.getZIndex() !== null ? a.getZIndex() : 0;
      const zib = b.getZIndex() !== null ? b.getZIndex() : 0;
      if (zia > zib) {
        res = 1;
      } else if (zia < zib) {
        res = -1;
      }

      return res;
    });

    return (new Promise((success, fail) => {
      const encodedLayers = [];
      layers.forEach((layer, index) => {
        this.getImpl().encodeLayer(layer, bbox, projection).then((encodedLayer) => {
          if (!M.utils.isNullOrEmpty(encodedLayer)) {
            encodedLayers[index] = encodedLayer;
          }

          numLayersToProc -= 1;
          if (numLayersToProc === 0) {
            // Mapfish requires reverse order
            success(encodedLayers.reverse());
          }
        });
      });
    }));
  }

  /**
   * This function encodes layers for georef image.
   *
   * @private
   * @function
   */
  encodeLayersGeo() {
    // Filters WMS and WMTS visible layers whose resolution is inside map resolutions range
    // and that doesn't have Cluster style.
    let layers = this.map_.getLayers().filter((layer) => {
      return (layer.isVisible() && layer.inRange() && layer.name !== 'cluster_cover' && layer.name !== 'selectLayer' && layer.name !== 'empty_layer' && ['WMS', 'WMTS', 'TMS', 'XYZ'].indexOf(layer.type) > -1);
    });

    if (this.map_.getZoom() === 20) {
      let contains = false;
      layers.forEach((l) => {
        if (l.url !== undefined && l.url === 'https://tms-pnoa-ma.idee.es/1.0.0/pnoa-ma/{z}/{x}/{-y}.jpeg') {
          contains = true;
        }
      });

      if (contains) {
        layers = layers.filter((l) => {
          return l.url !== 'https://tms-pnoa-ma.idee.es/1.0.0/pnoa-ma/{z}/{x}/{-y}.jpeg';
        });
      }
    } else if (this.map_.getZoom() < 20) {
      let contains = false;
      layers.forEach((l) => {
        if (l.url !== undefined && l.name !== undefined && l.url === 'https://www.ign.es/wmts/pnoa-ma?' && l.name === 'OI.OrthoimageCoverage') {
          contains = true;
        }
      });

      if (contains) {
        layers = layers.filter((l) => {
          return l.url !== 'https://www.ign.es/wmts/pnoa-ma?' && l.name !== 'OI.OrthoimageCoverage';
        });
      }
    }

    const encodedLayersModified = [];
    if (this.projection_.value === 'EPSG:3857') {
      for (let i = 0; i < layers.length; i += 1) {
        if (layers[i].matrixSet != null) {
          const matrixSet = layers[i].matrixSet.replace(layers[i].matrixSet, 'GoogleMapsCompatible');
          const optsMatrixSet = layers[i].options.matrixSet.replace(layers[i].matrixSet, 'GoogleMapsCompatible');
          layers[i].matrixSet = matrixSet;
          layers[i].options.matrixSet = optsMatrixSet;
        }

        encodedLayersModified.push(layers[i]);
      }

      layers = encodedLayersModified;
    } else {
      for (let i = 0; i < layers.length; i += 1) {
        if (layers[i].matrixSet != null) {
          const matrixSet = layers[i].matrixSet
            .replace(layers[i].matrixSet, this.projection_.value);
          const optsMatrixSet = layers[i].options.matrixSet
            .replace(layers[i].matrixSet, this.projection_.value);
          layers[i].matrixSet = matrixSet;
          layers[i].options.matrixSet = optsMatrixSet;
        }

        encodedLayersModified.push(layers[i]);
      }

      layers = encodedLayersModified;
    }

    let numLayersToProc = layers.length;
    const otherLayers = this.getImpl().getParametrizedLayers('IMAGEN', layers);
    if (otherLayers.length > 0) {
      layers = layers.concat(otherLayers);
      numLayersToProc = layers.length;
    }

    layers = layers.sort((a, b) => {
      let res = 0;
      const zia = a.getZIndex() !== null ? a.getZIndex() : 0;
      const zib = b.getZIndex() !== null ? b.getZIndex() : 0;
      if (zia > zib) {
        res = 1;
      } else if (zia < zib) {
        res = -1;
      }

      return res;
    });

    return (new Promise((success, fail) => {
      const encodedLayers = [];
      layers.forEach((layer, index) => {
        this.getImpl().encodeLayer(layer, null).then((encodedLayer) => {
          if (!M.utils.isNullOrEmpty(encodedLayer)) {
            encodedLayers[index] = encodedLayer;
          }

          numLayersToProc -= 1;
          if (numLayersToProc === 0) {
            // Mapfish requires reverse order
            success(encodedLayers.reverse());
          }
        });
      });
    }));
  }

  /**
   * This function downloads printed map.
   *
   * @public
   * @function
   * @api stable
   */
  downloadPrint(event) {
    event.preventDefault();
    const downloadUrl = this.getAttribute(PrinterMapControlihm.DOWNLOAD_ATTR_NAME);
    if (!M.utils.isNullOrEmpty(downloadUrl)) {
      window.open(downloadUrl, '_blank');
    }
  }

  /**
   * This function downloads geo printed map.
   *
   * @public
   * @function
   * @api stable
   */
  downloadGeoPrint(bbox, event) {
    const base64image = this.getBase64Image(this.documentRead_.src);
    base64image.then((resolve) => {
      const size = this.map_.getMapImpl().getSize();
      const Px = (((bbox[2] - bbox[0]) / size[0]) * (72 / this.dpiGeo_)).toString();
      const GiroA = (0).toString();
      const GiroB = (0).toString();
      const Py = (-((bbox[3] - bbox[1]) / size[1]) * (72 / this.dpiGeo_)).toString();
      const Cx = (bbox[0] + (Px / 2)).toString();
      const Cy = (bbox[3] + (Py / 2)).toString();
      const f = new Date();
      const titulo = 'mapa_'.concat(f.getFullYear(), '-', f.getMonth() + 1, '-', f.getDay() + 1, '_', f.getHours(), f.getMinutes(), f.getSeconds());
      const zip = new JsZip();
      zip.file(titulo.concat('.jgw'), Px.concat('\n', GiroA, '\n', GiroB, '\n', Py, '\n', Cx, '\n', Cy));
      zip.file(titulo.concat('.jpg'), resolve, { base64: true });
      zip.generateAsync({ type: 'blob' }).then((content) => {
        saveAs(content, titulo.concat('.zip'));
      });
    }).catch((err) => {
      M.dialog.error(getValue('exception.imageError'));
    });
  }

  getBase64Image(imgUrl) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.setAttribute('crossorigin', 'anonymous');
      img.src = imgUrl;
      img.onload = function can() {
        this.canvas_ = document.createElement('canvas');
        this.canvas_.width = img.width;
        this.canvas_.height = img.height;
        const ctx = this.canvas_.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const dataURL = this.canvas_.toDataURL('image/jpeg', 1.0);
        resolve(dataURL.replace(/^data:image\/(png|jpeg);base64,/, ''));
      };

      img.onerror = function rej() {
        Promise.reject(new Error(getValue('exception.loaderror')));
      };
    });
  }

  /**
   *  Converts epsg code to projection name.
   * @public
   * @function
   * @param {String} projection - EPSG:xxxx
   * @api
   */
  turnProjIntoLegend(projection) {
    let projectionLegend;
    switch (projection) {
      case 'EPSG:4258':
        projectionLegend = 'EPSG:4258 (ETRS89)';
        break;
      case 'EPSG:4326':
        projectionLegend = 'EPSG:4326 (WGS84)';
        break;
      case 'EPSG:3857':
        projectionLegend = 'EPSG:3857 (WGS84)';
        break;
      case 'EPSG:25831':
        projectionLegend = `EPSG:25831 (UTM ${getValue('zone')} 31N)`;
        break;
      case 'EPSG:25830':
        projectionLegend = `EPSG:25830 (UTM ${getValue('zone')} 30N)`;
        break;
      case 'EPSG:25829':
        projectionLegend = `EPSG:25829 (UTM ${getValue('zone')} 29N)`;
        break;
      case 'EPSG:25828':
        projectionLegend = `EPSG:25828 (UTM ${getValue('zone')} 28N)`;
        break;
      default:
        projectionLegend = '';
    }
    return projectionLegend;
  }

  toggleTabs(evt) {
    if (evt.currentTarget.classList.contains('inactive')) {
      const buttons = document.querySelectorAll('.tab-button');
      buttons.forEach((b) => {
        if (b.classList.contains('inactive')) {
          b.classList.remove('inactive');
        } else {
          b.classList.add('inactive');
        }
      });

      const tabs = document.querySelectorAll('.tab-content');
      tabs.forEach((t) => {
        if (t.classList.contains('closed')) {
          t.classList.remove('closed');
        } else {
          t.classList.add('closed');
        }
      });
    }
  }

  addMapClickEvent() {
    // this.createLayer();
    const classList = document.querySelector('#select-extent').classList;
    if (classList.contains('active')) {
      classList.remove('active');
      this.map_.un(M.evt.CLICK, this.funClickEvent);
    } else if (this.validateScale(document.querySelector('#scale').value)) {
      classList.add('active');
      this.funClickEvent = this.createExtent.bind(this);
      this.map_.on(M.evt.CLICK, this.funClickEvent);
      const classListFigure = document.querySelector('#draw-figure').classList;
      if (classListFigure.contains('active')) {
        classListFigure.remove('active');
        this.getImpl().removeFigureDrawInteraction();
      }
    }
  }

  addMapFigureEvent() {
    // this.createLayer();
    const classList = document.querySelector('#draw-figure').classList;
    if (classList.contains('active')) {
      classList.remove('active');
      this.getImpl().removeFigureDrawInteraction();
    } else {
      classList.add('active');
      this.getImpl().addFigureInteraction();
      const classListClick = document.querySelector('#select-extent').classList;
      if (classListClick.contains('active')) {
        classListClick.remove('active');
        this.map_.un(M.evt.CLICK, this.funClickEvent);
      }
    }
  }

  createLayer() {
    if (!this.extentLayer) {
      this.extentLayer = new M.layer.Vector({
        name: 'ExtentLayer',
        displayInLayerSwitcher: false,
      });
      this.extentLayer.displayInLayerSwitcher = false;
      this.map_.addLayers(this.extentLayer);
      this.extentLayer.on(M.evt.SELECT_FEATURES, this.showPrintFeatureInfo.bind(this), false);
    }
  }

  showPrintFeatureInfo(features) {
    if (M.utils.isUndefined(features[0])) {
      return true;
    }
    const extent = features[0].getImpl().getOLFeature().getGeometry().getExtent();
    const coordinates = [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2];
    const attributes = features[0].getAttributes();
    const projection = attributes.proyeccion === 'EPSG:3857' ? attributes.proyeccion : `EPSG:3395 (${getValue('calc')})`;
    const content = `<div style='width: max-content'>
      <div style='display: flex'>
        <div style='float: left; margin-right: 15px'>
          <p style='font-weight: bold; color: #193063'>${attributes.titulo}</p>
          <p>${attributes.descripcion}</p>
          <p>${getValue('scale')}: 1:${attributes.escala}</p>
          <p>${getValue('projection')}: ${projection}</p>
          <p>${getValue('layout')}: ${attributes.plantilla}</p>
        </div>
      </div>`;
    const featureTabOpts = {
      icon: 'g-cartografia-pin',
      title: '',
      content,
    };

    const popup = new M.Popup({
      panMapIfOutOfView: true,
      ani: null,
    });
    popup.addTab(featureTabOpts);
    this.map_.addPopup(popup, coordinates);
    return true;
  }

  validateForm() {
    let valid = true;
    const title = document.querySelector('#title').value;
    const description = document.querySelector('#description').value;
    const scale = document.querySelector('#scale').value;

    if (!title) {
      valid = false;
      M.dialog.error('El título es obligatorio');
    } else if (!description) {
      valid = false;
      M.dialog.error('La descripción es obligatoria');
    } else if (!this.validateScale(scale)) {
      valid = false;
      M.dialog.error('La escala no es correcta');
    }

    return valid;
  }

  validateScale(scale) {
    let valid = true;
    if (scale !== '' && scale <= 0) {
      valid = false;
    }

    return valid;
  }

  onDrawFigure(evt) {
    document.querySelector('#draw-figure').classList.remove('active');
    const olFeature = evt.feature;
    const mFeature = new M.Feature();
    const geometry = { type: 'Polygon', coordinates: olFeature.getGeometry().getCoordinates() };
    mFeature.setGeometry(geometry);
    this.setAttributesAndStyleFeature(mFeature, true);
    this.setFinalBbox(mFeature);
    this.addToExtentLayer(mFeature);
    this.getImpl().removeFigureDrawInteraction();
  }

  setFinalBbox(feature) {
    const featureCopy = feature;
    const scale = featureCopy.getAttribute('escala');
    const layout = featureCopy.getAttribute('plantilla');
    const bbox = featureCopy.getImpl().getOLFeature().getGeometry().getExtent();
    const center = [(bbox[0] + bbox[2]) / 2, (bbox[1] + bbox[3]) / 2];
    const coords = this.calculateExtent(center, scale, layout);
    featureCopy.finalCoords = coords;
  }

  createExtent(evt) {
    this.map_.un(M.evt.CLICK, this.funClickEvent);
    document.querySelector('#select-extent').classList.remove('active');

    const scale = document.querySelector('#scale').value;
    const layout = document.querySelector('#layout').value;

    const coords = this.calculateExtent(evt.coord, scale, layout);
    const geometry = { type: 'Polygon', coordinates: coords };
    const feature = new M.Feature();
    feature.setGeometry(geometry);
    this.setAttributesAndStyleFeature(feature, false);
    this.addToExtentLayer(feature);
  }

  setAttributesAndStyleFeature(feature, dibujoLibre) {
    const title = document.querySelector('#title').value || `Chart_${this.chartIndex += 1}`;
    const description = document.querySelector('#description').value; // || this.credits_;
    const projectionVal = document.querySelector('#projections').value;
    const layout = document.querySelector('#layout').value;
    const scale = this.getScale(dibujoLibre, layout, feature);
    const dpiVal = document.querySelector('#dpi').value;
    const attributes = {
      titulo: title,
      descripcion: description,
      escala: scale,
      plantilla: layout,
      dpi: dpiVal,
      proyeccion: projectionVal,
    };
    feature.setAttributes(attributes);
    feature.setStyle(this.printStyle);
  }

  getScale(dibujoLibre, layout, feature) {
    let scale = document.querySelector('#scale').value || '50000';
    if (dibujoLibre) {
      scale = this.calculateScale(layout, feature);
    }
    return scale;
  }

  calculateScale(layout, feature) {
    const projection = document.querySelector('#projections').value;
    const size = this.getLayoutSize(layout);
    let extent = feature.getImpl().getOLFeature().getGeometry().getExtent();
    let center = [(extent[2] + extent[0]) / 2, (extent[3] + extent[1]) / 2];
    if (projection === 'EPSG:33952') {
      const lonlat = ol.proj.transform(center, this.map_.getProjection().code, 'EPSG:4326');
      const proj4Str = `+proj=merc +lat_ts=${lonlat[1]} +lon_0=0 +datum=WGS84 +units=m +no_defs`;
      const coordMin = proj4('EPSG:3857', proj4Str, [extent[0], extent[1]]);
      const coordMax = proj4('EPSG:3857', proj4Str, [extent[2], extent[3]]);
      extent = [coordMin[0], coordMin[1], coordMax[0], coordMax[1]];
      center = [(extent[2] + extent[0]) / 2, (extent[3] + extent[1]) / 2];
    }
    const layoutAspectRatio = size[0] / size[1];
    const bboxAspectRatio = (extent[2] - extent[0]) / (extent[3] - extent[1]);
    if (layoutAspectRatio > bboxAspectRatio) {
      const factor = layoutAspectRatio / bboxAspectRatio;
      const diff = (extent[2] - center[0]) * factor;
      extent = [center[0] - diff, extent[1], center[0] + diff, extent[3]];
    } else {
      const factor = bboxAspectRatio / layoutAspectRatio;
      const diff = (extent[3] - center[1]) * factor;
      extent = [extent[0], center[1] - diff, extent[2], center[1] + diff];
    }
    const resolution = Math.abs((extent[0] - extent[2]) / size[0]);
    let scale = M.utils.getScaleFromResolution(resolution, 'm');
    if (scale) {
      if (scale >= 1000 && scale <= 950000) {
        scale = Math.round(scale / 1000) * 1000;
      } else if (scale >= 950000) {
        scale = Math.round(scale / 1000000) * 1000000;
      } else {
        scale = Math.round(scale);
      }
    }
    return scale;
  }

  calculateExtent(center, scale, layout) {
    const projection = document.querySelector('#projections').value;
    const sizeProp = this.calculateSizeProp(layout);
    const escalaActual = this.map_.getExactScale();
    const escala = scale || 50000;
    const scaleFactor = (escala / escalaActual);
    const bbox = this.map_.getBbox();
    const centerMap = this.map_.getCenter();

    let xDif = (centerMap.x - bbox.x.min) * scaleFactor * sizeProp[0];
    let minX = center[0] - xDif;
    let maxX = center[0] + xDif;

    let yDif = (centerMap.y - bbox.y.min) * scaleFactor * sizeProp[1];
    let minY = center[1] - yDif;
    let maxY = center[1] + yDif;

    if (projection === 'EPSG:33952') {
      const size = this.getLayoutSize(layout);
      const resolution = Math.abs((minX - maxX) / size[0]);
      const lonlat = ol.proj.transform(center, this.map_.getProjection().code, 'EPSG:4326');
      const proj4Str = `+proj=merc +lat_ts=${lonlat[1]} +lon_0=0 +datum=WGS84 +units=m +no_defs`;
      let coordMin = proj4('EPSG:3857', proj4Str, [minX, minY]);
      let coordMax = proj4('EPSG:3857', proj4Str, [maxX, maxY]);
      coordMin[0] = coordMax[0] - (resolution * size[0]);
      coordMin[1] = coordMax[1] - (resolution * size[1]);
      coordMin = proj4(proj4Str, 'EPSG:3857', coordMin);
      coordMax = proj4(proj4Str, 'EPSG:3857', coordMax);
      // con el recalculo el centro del recuadro se ha desplazado con respecto al click en el mapa
      // y hay que desplazar el recuadro para que su centro se corresponda con el click en el mapa
      xDif = center[0] - ((coordMin[0] + coordMax[0]) / 2);
      yDif = center[1] - ((coordMin[1] + coordMax[1]) / 2);
      minX = coordMin[0] + xDif;
      maxX = coordMax[0] + xDif;
      minY = coordMin[1] + yDif;
      maxY = coordMax[1] + yDif;
    }
    const coordinates = [[
      [minX, maxY], [maxX, maxY],
      [maxX, minY], [minX, minY],
      [minX, maxY],
    ]];

    return coordinates;
  }

  calculateSizeProp(layout) {
    const prop = [1, 1];
    const size = this.getLayoutSize(layout);
    const mapContainer = this.map_.getContainer();
    if (size && size.length > 0) {
      if (size[0] > 0 && size[1] > 0) {
        prop[0] = size[0] / mapContainer.clientWidth;
        prop[1] = size[1] / mapContainer.clientHeight;
      }
    }

    return prop;
  }

  getLayoutSize(layout) {
    const index = this.layoutOptions_.indexOf(layout);
    const size = this.layoutMapSize_[index].split(';');
    return size;
  }

  addToExtentLayer(feature) {
    this.extentLayer.addFeatures(feature);
    this.addFeatureToQueue(feature);
  }

  addFeatureToQueue(feature) {
    const queueTable = document.querySelector('#queue-table');
    const col1 = document.createElement('td');
    const input = document.createElement('input');
    input.value = feature.getAttribute('titulo');
    input.id = `input-${feature.getId()}`;
    input.addEventListener('keyup', this.changeFeatureTitle.bind(this));
    col1.appendChild(input);
    const col2 = document.createElement('td');
    col2.classList.add('progress-col');
    const row = document.createElement('tr');
    row.id = feature.getId();
    row.appendChild(col1);
    row.appendChild(col2);
    queueTable.appendChild(row);
  }

  changeFeatureTitle(evt) {
    const target = evt.currentTarget;
    const feat = this.extentLayer.getFeatureById(target.id.substring(6));
    feat.setAttribute('titulo', target.value);
  }

  selectQueueElement(evt) {
    const elem = evt.srcElement;// columna
    const parent = elem.parentNode;// fila
    if (parent.id.startsWith('mapea_feature') && !parent.lastChild.classList.contains(PrinterMapControlihm.LOADING_CLASS)) {
      if (parent.classList.contains('selected')) {
        parent.classList.remove('selected');
      } else {
        parent.classList.add('selected');
      }
    }
  }

  toggleMoveTool() {
    // this.createLayer();
    const moveTools = document.querySelectorAll('.move-extent');
    let active = true;
    moveTools.forEach((t) => {
      if (t.classList.contains('active')) {
        t.classList.remove('active');
        active = false;
      } else {
        t.classList.add('active');
        active = true;
      }
    });
    if (active) {
      this.getImpl().activateEdition();
    } else {
      this.getImpl().removeEditInteraction();
    }
  }

  clearForm(event) {
    event.preventDefault();
    // reset values
    document.querySelector('#title').value = '';
    document.querySelector('#description').value = '';
    document.querySelector('#scale').value = '';
    document.querySelector('#layout').value = this.layoutOptions_[0];
    document.querySelector('#dpi').value = this.dpisOptions_[0];
    this.projection_ = 'EPSG:3857';
  }

  deleteFromQueue() {
    const elements = document.querySelectorAll('#queue-table>tr.selected');
    if (elements.length > 0) {
      const parent = elements[0].parentNode;
      const features = [];
      elements.forEach((e) => {
        const feat = this.extentLayer.getFeatureById(e.id);
        features.push(feat);
        parent.removeChild(e);
      });
      this.extentLayer.removeFeatures(features);
    }
  }

  /**
   * This function checks if an object is equal to this control.
   *
   * @function
   * @api stable
   */
  equals(obj) {
    let equals = false;
    if (obj instanceof PrinterMapControlihm) {
      equals = (this.name === obj.name);
    }
    return equals;
  }
}

/**
 * Name for this controls
 * @const
 * @type {string}
 * @public
 * @api stable
 */
PrinterMapControlihm.NAME = 'printermapcontrolihm';

/**
 * M.template for this controls
 * @const
 * @type {string}
 * @public
 * @api stable
 */
PrinterMapControlihm.TEMPLATE = 'printermapihm.html';

/**
 * M.template for this controls
 * @const
 * @type {string}
 * @public
 * @api stable
 */
PrinterMapControlihm.LOADING_CLASS = 'printing';

/**
 * M.template for this controls
 * @const
 * @type {string}
 * @public
 * @api stable
 */
PrinterMapControlihm.DOWNLOAD_ATTR_NAME = 'data-donwload-url-print';
